import { useEffect, useRef, useState } from 'react';
import type { LoadOptions } from './types';

export interface UseAutocompleteQueryOptions<T> {
  query: string;
  loadOptions: LoadOptions<T>;
  debounceMs?: number;
  minQueryLength?: number;
  enabled?: boolean;
}

export interface UseAutocompleteQueryResult<T> {
  items: T[];
  loading: boolean;
  error: Error | null;
}

/**
 * Debounced async loader for Autocomplete remote sources. Owns timing,
 * AbortController-based cancellation, and stale-response dropping by
 * monotonic request id (so out-of-order resolves cannot clobber the
 * latest query).
 */
export function useAutocompleteQuery<T>(
  opts: UseAutocompleteQueryOptions<T>,
): UseAutocompleteQueryResult<T> {
  const { query, loadOptions, debounceMs = 250, minQueryLength = 0, enabled = true } = opts;

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const requestId = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (query.length === 0) {
      setItems([]);
      setLoading(false);
      setError(null);
      abortRef.current?.abort();
      return;
    }
    if (query.length < minQueryLength) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const id = ++requestId.current;
    const handle = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      loadOptions(query, controller.signal)
        .then((next) => {
          if (id !== requestId.current) return;
          setItems(next);
          setLoading(false);
          setError(null);
        })
        .catch((e: unknown) => {
          if (id !== requestId.current) return;
          if (e instanceof Error && e.name === 'AbortError') return;
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
        });
    }, debounceMs);

    return () => clearTimeout(handle);
  }, [query, loadOptions, debounceMs, minQueryLength, enabled]);

  return { items, loading, error };
}
